import { useState } from 'react';
import type { SendableNotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type { NotificationKindChoice } from './useNotificationKind.ts';

/** The one moment that names the reader's colleagues, and so takes people never to name. */
const ASK_WUNDO_KIND = 'ask_wundo_nugget';

/** The one moment about a roleplay, which cannot be sent without one. */
const ROLEPLAY_KIND = 'roleplay_nugget';

const EMAIL_ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What staff set for this send, as it is applied: options the kind does not take are left out. */
export interface SendOptions {
  /** The channel to send on instead of each person's own; undefined is their own. */
  channel: SendableNotificationChannel | undefined;
  /** Send it again to whoever already received it; every other reason still leaves people out. */
  resend: boolean;
  takesNeverName: boolean;
  /** Who the ask-Wundo tip must never name, applied when the field is left. */
  neverName: string[];
  neverNameText: string;
  neverNameError: string;
  takesRoleplay: boolean;
  roleplayId: string | undefined;
  chooseChannel: (channel: SendableNotificationChannel | '') => void;
  chooseResend: (resend: boolean) => void;
  chooseRoleplay: (roleplayId: string) => void;
  typeNeverName: (text: string) => void;
  applyNeverName: () => void;
  reset: () => void;
}

/* Addresses one per line or separated by commas; whatever is not one is named back. */
function readAddresses(text: string): { addresses: string[]; invalid: string[] } {
  const entries = [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];

  return {
    addresses: entries.filter((entry) => EMAIL_ADDRESS.test(entry)),
    invalid: entries.filter((entry) => !EMAIL_ADDRESS.test(entry)),
  };
}

export function useSendOptions(kind: NotificationKindChoice): SendOptions {
  const [channel, setChannel] = useState<SendableNotificationChannel | ''>('');
  const [resend, setResend] = useState(false);
  /* Kept as typed and as applied, so the preview is not redrawn on every keystroke. */
  const [neverNameText, setNeverNameText] = useState('');
  const [neverName, setNeverName] = useState<string[]>([]);
  const [roleplayId, setRoleplayId] = useState('');

  const kindChannels = kind.entry?.channels ?? [];
  const takesNeverName = kind.kind === ASK_WUNDO_KIND;
  const takesRoleplay = kind.kind === ROLEPLAY_KIND;
  const typedNeverName = readAddresses(neverNameText);

  const applyNeverName = () => {
    if (typedNeverName.invalid.length === 0) {
      setNeverName(typedNeverName.addresses);
    }
  };

  const reset = () => {
    setNeverNameText('');
    setNeverName([]);
    setRoleplayId('');
    setChannel('');
    setResend(false);
  };

  return {
    /* A mail has one channel and can always be sent again, so it takes neither. */
    channel:
      !kind.isEmail && channel !== '' && kindChannels.includes(channel)
        ? channel
        : undefined,
    resend: !kind.isEmail && resend,
    takesNeverName,
    neverName: takesNeverName ? neverName : [],
    neverNameText,
    neverNameError:
      typedNeverName.invalid.length > 0
        ? `Not an e-mail address: ${typedNeverName.invalid.join(', ')}`
        : '',
    takesRoleplay,
    roleplayId: takesRoleplay && roleplayId !== '' ? roleplayId : undefined,
    chooseChannel: setChannel,
    chooseResend: setResend,
    chooseRoleplay: setRoleplayId,
    typeNeverName: setNeverNameText,
    applyNeverName,
    reset,
  };
}
